import { Page, Card, DataTable, Button, Badge, Text, BlockStack } from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { createApiUrl } from '../config/api';
import { useApiClient } from '../hooks/useApiClient';

interface Label {
  _id?: string;
  id?: string;
  name: string;
  text: string;
  enabled: boolean;
  type: string;
  productsCount: number;
  createdAt: string;
}

export default function LabelsList() {
  const navigate = useAppNavigate();
  const api = useApiClient();
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLabels();
  }, []);

  const fetchLabels = async () => {
    try {
      const data = await api.get<Label[]>(createApiUrl('api/labels'));
      setLabels(data);
    } catch (error) {
      console.error('Error fetching labels:', error);
      alert(`Error fetching labels: ${(error as any)?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteLabel = async (id: string) => {
    if (!confirm('Are you sure you want to delete this label?')) return;

    try {
      await api.delete(createApiUrl(`api/labels/${id}`));
      setLabels(labels.filter((label) => (label._id || label.id) !== id));
    } catch (error) {
      console.error('Error deleting label:', error);
      alert(`Error deleting label: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const toggleLabel = async (id: string, enabled: boolean) => {
    try {
      await api.patch(createApiUrl(`api/labels/${id}`), { enabled: !enabled });
      setLabels(
        labels.map((label) =>
          (label._id || label.id) === id ? { ...label, enabled: !enabled } : label
        )
      );
    } catch (error) {
      console.error('Error toggling label:', error);
      alert(`Error updating label: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const rows = labels.map((label) => {
    const labelId = (label._id || label.id) as string;
    return [
    label.name,
    label.text,
    label.type,
    <Badge tone={label.enabled ? 'success' : 'critical'}>
      {label.enabled ? 'Active' : 'Inactive'}
    </Badge>,
    label.productsCount,
    new Date(label.createdAt).toLocaleDateString(),
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button size="slim" onClick={() => navigate(`/labels/${labelId}`)}>
        Edit
      </Button>
      <Button
        size="slim"
        tone={label.enabled ? 'critical' : 'success'}
        onClick={() => toggleLabel(labelId, label.enabled)}
      >
        {label.enabled ? 'Disable' : 'Enable'}
      </Button>
      <Button size="slim" tone="critical" onClick={() => deleteLabel(labelId)}>
        Delete
      </Button>
    </div>,
    ];
  });

  return (
    <Page
      title="Labels"
      primaryAction={{
        content: 'Create Label',
        onAction: () => navigate('/labels/new'),
      }}
      backAction={{ onAction: () => navigate('/') }}
    >
      <Card>
        {labels.length === 0 && !loading ? (
          <BlockStack gap="400" inlineAlign="center">
            <Text as="p" variant="bodyMd">
              No labels found. Create your first label to get started!
            </Text>
            <Button onClick={() => navigate('/labels/new')}>Create Label</Button>
          </BlockStack>
        ) : (
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
            headings={['Name', 'Text', 'Type', 'Status', 'Products', 'Created', 'Actions']}
            rows={rows}
          />
        )}
      </Card>
    </Page>
  );
}
